import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { object, number, string, date, InferType } from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Listbox,
    ListboxSection,
    ListboxItem,
    Button,
    Selection,
} from "@heroui/react";
import { PlantingPlan, SimulatedCropPlan } from "@/types/cropPlan";
import {
    PlantingOperation,
    DEFAULT_SEEDING_RATE,
    DEFAULT_PLANTING_DEPTH,
} from "@/types/operations";
import { CropConfiguration } from "@/types/cropConfiguration";
import { useMemo, useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faPenToSquare,
    faCheckCircle,
    faClone,
    faArrowRightToBracket,
    faBookmark,
} from "@fortawesome/free-solid-svg-icons";
import { parseDate } from "@internationalized/date";
import TextInput from "@/components/inputs/TextInput";
import SelectInput from "@/components/inputs/SelectInput";
import DateInput from "@/components/inputs/DateInput";
import NumberInput from "@/components/inputs/NumberInput";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import { UseQueryResult } from "@tanstack/react-query";


const DEFAULT_CROP_CONFIGURATION_ID = 1;
const SENTINEL_ORGANIZATION_ID = 1;

const plantingPlanSchema = object({
    id: number().nullable(),
    date: date().required(),
    crop_configuration: number().required(),
    crm: number().required(),
    hybrid: string().nullable(),
    seeding_rate: number().required(),
    seeding_depth: number().nullable(),
    name: string().required(),
    organization: number().required(),
});

type PlantingPlanFormValues = InferType<typeof plantingPlanSchema>;


export function PlantingPlanForm({
    organizations,
    activePlantingOperationId,
    selectedPlantingPlanId,
    setSelectedPlantingPlanId,
    plantingPlansRequest,
    cropsQuery,
    primaryCropPlan,
}: {
    organizations: { id: number; description: string }[];
    activePlantingOperationId?: number;
    selectedPlantingPlanId: Selection;
    setSelectedPlantingPlanId: (selection: Selection) => void;
    plantingPlansRequest: UseQueryResult<PlantingPlan[]>;
    cropsQuery: UseQueryResult<CropConfiguration[]>;
    primaryCropPlan?: SimulatedCropPlan;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();

    const { user, userIsSentinel, isGrower } = useAuth();

    const userOrganizationId: number | undefined = useMemo(() => {
        if (typeof user?.organization === "object") {
            return user.organization.id;
        }
        return typeof user?.organization === "string"
            ? parseInt(user.organization)
            : user?.organization;
    }, [user]);

    const defaultPlantingDate = useMemo(() => {
        if (!cropsQuery.data) {
            return undefined;
        }
        const defaultCrop = cropsQuery.data.find(
            (crop) => crop.id === DEFAULT_CROP_CONFIGURATION_ID,
        );
        if (defaultCrop?.default_planting_date) {
            return dayjs(defaultCrop.default_planting_date).toDate();
        }
        return undefined;
    }, [cropsQuery.data]);

    const cropOptions = useMemo(() => {
        if (!cropsQuery.data) {
            return [];
        }
        return cropsQuery.data
            .sort((a, b) => a.crop_name.localeCompare(b.crop_name))
            .map((crop) => ({
                id: crop.id,
                description: crop.crop_name,
            }));
    }, [cropsQuery.data]);

    const plantingPlanForm = useForm<PlantingPlanFormValues>({
        resolver: yupResolver(plantingPlanSchema),
        defaultValues: {
            id: null,
            organization: userOrganizationId,
            date: defaultPlantingDate,
            crop_configuration: DEFAULT_CROP_CONFIGURATION_ID,
            crm: 100,
            hybrid: null,
            seeding_rate: DEFAULT_SEEDING_RATE,
            seeding_depth: DEFAULT_PLANTING_DEPTH,
        },
    });

    const twoMonthsAgo = useMemo(() => {
        return parseDate(dayjs().subtract(2, "month").format("YYYY-MM-DD"));
    }, []);

    const [plantingPlanId, plantingPlanOrganization] =
        plantingPlanForm.watch(["id", "organization"]);

    const plantingOperationRequest = useGetRequest<PlantingOperation>({
        url: `/planting_operations/${activePlantingOperationId}/`,
        enabled: !!activePlantingOperationId,
    });

    const existingPlantingPlanOrganizations = useMemo(() => {
        const orgIds = new Set<number>([SENTINEL_ORGANIZATION_ID]);
        if (plantingPlansRequest.data) {
            plantingPlansRequest.data.forEach((plan) => {
                orgIds.add(plan.organization);
            });
        }
        return (
            organizations.some(
                (org) => org.id === SENTINEL_ORGANIZATION_ID
            ) ?
                organizations.filter((org) => orgIds.has(org.id)) ?? [] :
                (organizations.filter((org) => orgIds.has(org.id)) ?? []).concat(
                    {
                        id: SENTINEL_ORGANIZATION_ID,
                        description: "Sentinel",
                    }
                )
        );
    }, [plantingPlansRequest.data, organizations]);

    const plantingPlansOptions = useMemo(() => {
        if (!plantingPlansRequest.data) {
            return [];
        }
        return plantingPlansRequest.data.map((plan) => ({
            ...plan,
            key: plan.id,
            group: plan.organization.toString(),
        }));
    }, [plantingPlansRequest.data]);

    const userCanEditPlantingPlan = useMemo(() => {
        return (
            userIsSentinel ||
            (!isGrower &&
                plantingPlanOrganization !== SENTINEL_ORGANIZATION_ID) ||
            !plantingPlanOrganization ||
            plantingPlanOrganization === userOrganizationId
        );
    }, [
        plantingPlanOrganization,
        userOrganizationId,
        userIsSentinel,
        isGrower,
    ]);

    const [editPlantingPlan, setEditPlantingPlan] = useState<boolean>(true);

    const savePlantingPlanMutation = useMutateRequest<PlantingPlan>({
        successMessage: "Planting Plan Saved",
        onSuccess: () => {
            setEditPlantingPlan(false);
            plantingPlansRequest.refetch();
        },
    });

    const handleSavePlantingPlan = useCallback(() => {
        const plantingData = {
            id: plantingPlanId,
            organization: plantingPlanForm.getValues("organization"),
            name: plantingPlanForm.getValues("name"),
            date: dayjs(plantingPlanForm.getValues("date")).format(
                "YYYY-MM-DD",
            ),
            crop_configuration:
                plantingPlanForm.getValues("crop_configuration"),
            seeding_rate: plantingPlanForm.getValues("seeding_rate"),
            seeding_depth: plantingPlanForm.getValues("seeding_depth"),
            varieties: [
                {
                    id: null,
                    name: plantingPlanForm.getValues("hybrid"),
                    crm: plantingPlanForm.getValues("crm"),
                },
            ],
        };
        if (plantingPlanId) {
            savePlantingPlanMutation.mutate({
                url: `/planting_plans/${plantingPlanId}/`,
                method: "PUT",
                data: plantingData,
            });
            return;
        } else {
            savePlantingPlanMutation.mutate({
                url: `/planting_plans/`,
                method: "POST",
                data: plantingData,
            });
            return;
        }
    }, [plantingPlanForm, savePlantingPlanMutation, plantingPlanId]);

    useEffect(() => {
        const selectedKey = Array.from(selectedPlantingPlanId)[0];
        const selectedPlan = plantingPlansOptions.find(
            (plan) => plan.key?.toString() === selectedKey,
        );
        if (selectedPlan) {
            plantingPlanForm.reset({
                id: selectedPlan.id,
                name: selectedPlan.name,
                date: dayjs(selectedPlan.date).toDate(),
                organization: selectedPlan.organization,
                crop_configuration: selectedPlan.crop_configuration,
                crm: selectedPlan.varieties?.[0]?.crm || undefined,
                hybrid: selectedPlan.varieties?.[0]?.name || undefined,
                seeding_rate: selectedPlan.seeding_rate || undefined,
                seeding_depth: selectedPlan.seeding_depth || undefined,
            });
        } else {
            plantingPlanForm.reset({
                id: null,
                name: undefined,
                crop_configuration: DEFAULT_CROP_CONFIGURATION_ID,
                crm: 100,
                date: defaultPlantingDate,
                organization: userOrganizationId,
                hybrid: null,
                seeding_rate: DEFAULT_SEEDING_RATE,
                seeding_depth: DEFAULT_PLANTING_DEPTH,
            });
        }
    }, [
        plantingPlansOptions,
        plantingPlanForm,
        defaultPlantingDate,
        selectedPlantingPlanId,
        userOrganizationId,
    ]);

    useEffect(() => {
        if (Array.from(selectedPlantingPlanId).length > 0) {
            setEditPlantingPlan(false);
        }
    }, [selectedPlantingPlanId]);


    return (
        <>
            <div className="font-bold text-2xl mb-2 text-primary-500">
                Planting
            </div>
            <div className="flex flex-row w-full gap-2">
                <div className="flex flex-col w-1/4 h-full gap-1">
                    <Listbox
                        items={plantingPlansOptions}
                        selectionMode="single"
                        color="primary"
                        variant="light"
                        className="border border-gray-300 rounded"
                        emptyContent="No Plans Found"
                        selectedKeys={
                            selectedPlantingPlanId
                        }
                        onSelectionChange={
                            setSelectedPlantingPlanId
                        }
                    >
                        {existingPlantingPlanOrganizations.map(
                            (org) => (
                                <ListboxSection
                                    title={org.description}
                                    key={org.id}
                                    showDivider={true}
                                    className="font-bold"
                                >
                                    {plantingPlansOptions.filter(
                                        (plan) =>
                                            plan.group ===
                                            org.id.toString(),
                                    )
                                        .map(
                                            (plan) => (
                                                <ListboxItem
                                                    key={
                                                        plan.key!
                                                    }
                                                    className={
                                                        plantingPlanId ===
                                                            plan.id
                                                            ? "bg-primary-500 bg-opacity-20 text-primary-700"
                                                            : ""
                                                    }
                                                    startContent={
                                                        org.id ===
                                                        SENTINEL_ORGANIZATION_ID && (
                                                            <img
                                                                src={
                                                                    allNHorizontalImage
                                                                }
                                                                alt="All N Horizontal"
                                                                className="h-5 object-cover"
                                                            />
                                                        )
                                                    }
                                                    endContent={
                                                        plan.id ===
                                                        primaryCropPlan
                                                            ?.planting_plan
                                                            ?.id && (
                                                            <FontAwesomeIcon
                                                                icon={faBookmark}
                                                                className="text-primary-500"
                                                            />
                                                        )
                                                    }
                                                >
                                                    {
                                                        plan.name
                                                    }
                                                </ListboxItem>
                                            ),
                                        )}
                                </ListboxSection>
                            ),
                        )}
                    </Listbox>
                    {activePlantingOperationId && (<Button
                        size="sm"
                        color="primary"
                        fullWidth
                        isDisabled={
                            !activePlantingOperationId
                        }
                        isLoading={
                            plantingOperationRequest.isLoading
                        }
                        onPress={() => {
                            if (
                                !plantingOperationRequest.data
                            )
                                return;
                            plantingPlanForm.reset(
                                {
                                    id: null,
                                    organization:
                                        userOrganizationId,
                                    name: "Import Logged Operation",
                                    date: dayjs(
                                        plantingOperationRequest
                                            .data
                                            .start_date,
                                    ).toDate(),
                                    crop_configuration:
                                        plantingOperationRequest
                                            .data
                                            .crop_configuration
                                            .id,
                                    seeding_rate:
                                        plantingOperationRequest
                                            .data
                                            .average_seeding_rate,
                                    seeding_depth:
                                        plantingOperationRequest
                                            .data
                                            .average_planting_depth,
                                    hybrid:
                                        plantingOperationRequest
                                            .data
                                            .varieties[0]
                                            ?.name ||
                                        "",
                                    crm:
                                        Number(
                                            plantingOperationRequest
                                                .data
                                                .varieties[0]
                                                ?.crm,
                                        ) ||
                                        undefined,
                                },
                            );
                            setEditPlantingPlan(
                                true,
                            );
                        }}
                    >
                        <FontAwesomeIcon
                            icon={
                                faArrowRightToBracket
                            }
                        />
                        Import Logged Operation
                    </Button>)}
                    <Button
                        size="sm"
                        color="primary"
                        fullWidth
                        onPress={() => {
                            plantingPlanForm.reset(
                                {
                                    id: null,
                                    organization:
                                        userOrganizationId,
                                    name: "New Planting Plan",
                                    date: defaultPlantingDate,
                                    crop_configuration:
                                        DEFAULT_CROP_CONFIGURATION_ID,
                                    seeding_rate:
                                        DEFAULT_SEEDING_RATE,
                                    seeding_depth:
                                        DEFAULT_PLANTING_DEPTH,
                                    hybrid: null,
                                    crm: 100,
                                },
                            );
                            setEditPlantingPlan(
                                true,
                            );
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faPlus}
                        />
                        New Planting Plan
                    </Button>
                </div>
                <div className="flex flex-col w-3/4">
                    <FormProvider {...plantingPlanForm}>
                        <form className="flex flex-col gap-1">
                            <div className="flex flex-row justify-between gap-1">
                                <TextInput
                                    name="name"
                                    label="Plan Name"
                                    labelPlacement="inside"
                                    size="sm"
                                    isReadOnly={
                                        !editPlantingPlan
                                    }
                                />
                                {!isGrower && (
                                    <AutoCompleteInput
                                        name="organization"
                                        label="Organization"
                                        labelPlacement="inside"
                                        options={
                                            organizations
                                        }
                                        size="sm"
                                        isReadOnly={
                                            !editPlantingPlan
                                        }
                                    />
                                )}
                                <SelectInput
                                    name="crop_configuration"
                                    label="Crop"
                                    labelPlacement="inside"
                                    options={
                                        cropOptions
                                    }
                                    size="sm"
                                    isDisabled={
                                        !editPlantingPlan
                                    }
                                />
                            </div>
                            <div className="flex flex-row justify-between gap-1">
                                <DateInput
                                    name="date"
                                    label="Planting Date"
                                    labelPlacement="inside"
                                    size="sm"
                                    isReadOnly={
                                        !editPlantingPlan
                                    }
                                    minValue={
                                        twoMonthsAgo
                                    }
                                />
                                <NumberInput
                                    name="crm"
                                    label="CRM"
                                    labelPlacement="inside"
                                    size="sm"
                                    isReadOnly={
                                        !editPlantingPlan
                                    }
                                />
                                <TextInput
                                    name="hybrid"
                                    label="Hybrid/Variety"
                                    labelPlacement="inside"
                                    size="sm"
                                    isReadOnly={
                                        !editPlantingPlan
                                    }
                                />
                                <NumberInput
                                    name="seeding_rate"
                                    label="Seeding Rate"
                                    labelPlacement="inside"
                                    size="sm"
                                    isReadOnly={
                                        !editPlantingPlan
                                    }
                                />
                                <NumberInput
                                    name="seeding_depth"
                                    label="Seeding Depth"
                                    labelPlacement="inside"
                                    size="sm"
                                    isReadOnly={
                                        !editPlantingPlan
                                    }
                                />
                            </div>
                            <div className="flex flex-row w-full justify-end gap-2 items-center mt-2">
                                {!editPlantingPlan &&
                                    plantingPlanId && (
                                        <Button
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={
                                                        faClone
                                                    }
                                                />
                                            }
                                            size="sm"
                                            isDisabled={
                                                Array.from(
                                                    selectedPlantingPlanId,
                                                )
                                                    .length ===
                                                0
                                            }
                                            onPress={() => {
                                                const selectedKey =
                                                    Array.from(
                                                        selectedPlantingPlanId,
                                                    )[0];
                                                const selectedPlan =
                                                    plantingPlansOptions.find(
                                                        (
                                                            plan,
                                                        ) =>
                                                            plan.key?.toString() ===
                                                            selectedKey,
                                                    );
                                                if (
                                                    selectedPlan
                                                ) {
                                                    const newPlanData =
                                                    {
                                                        ...selectedPlan,
                                                        id: null,
                                                        name: `${selectedPlan.name} Copy`,
                                                        organization:
                                                            userOrganizationId,
                                                    };
                                                    savePlantingPlanMutation.mutate(
                                                        {
                                                            url: `/planting_plans/`,
                                                            method: "POST",
                                                            data: newPlanData,
                                                        },
                                                        {
                                                            onSuccess:
                                                                (
                                                                    newPlan,
                                                                ) => {
                                                                    plantingPlansRequest
                                                                        .refetch()
                                                                        .then(
                                                                            () => {
                                                                                setSelectedPlantingPlanId(
                                                                                    new Set(
                                                                                        [
                                                                                            newPlan.id!.toString(),
                                                                                        ],
                                                                                    ),
                                                                                );
                                                                                setEditPlantingPlan(
                                                                                    true,
                                                                                );
                                                                            },
                                                                        );
                                                                },
                                                        },
                                                    );
                                                }
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    )}
                                {editPlantingPlan ? (
                                    <Button
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={
                                                    faCheckCircle
                                                }
                                                className="mr-1"
                                            />
                                        }
                                        color="primary"
                                        size="sm"
                                        isDisabled={
                                            !userCanEditPlantingPlan
                                        }
                                        isLoading={
                                            savePlantingPlanMutation.isPending
                                        }
                                        onPress={() => {
                                            plantingPlanForm.trigger();
                                            plantingPlanForm.handleSubmit(
                                                handleSavePlantingPlan,
                                            )();
                                        }}
                                    >
                                        Save Planting
                                        Plan
                                    </Button>
                                ) : (
                                    <Button
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={
                                                    faPenToSquare
                                                }
                                                className="mr-1"
                                            />
                                        }
                                        size="sm"
                                        isDisabled={
                                            !userCanEditPlantingPlan
                                        }
                                        onPress={() => {
                                            setEditPlantingPlan(
                                                true,
                                            );
                                        }}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </>
    );
}
